import { createFileRoute, redirect } from '@tanstack/react-router'
import i18next from 'i18next'

import { BillingOverviewLoading, BillingOverviewPage } from '@/components/Billing/BillingOverviewPage'
import { BillingPeriodSwitcher } from '@/components/Billing/BillingPeriodSelect'
import ErrorState from '@/components/Common/ErrorState'
import PageShell from '@/components/Common/PageShell'
import { QueryBoundary } from '@/components/Common/QueryBoundary'
import { requirePermission } from '@/utils/auth'

const isValidPeriodId = (value: string) => /^\d+$/.test(value)

export const Route = createFileRoute('/billing/$billingPeriodId')({
  beforeLoad: ({ params }) => {
    requirePermission('billing')

    if (!isValidPeriodId(params.billingPeriodId)) {
      throw redirect({ to: '/billing' })
    }
  },
  component: BillingPeriodRoute,
  errorComponent: ({ error }) => (
    <PageShell title={i18next.t('title', { ns: 'billing' })} fullWidth>
      <ErrorState
        title={i18next.t('failedToLoad', { ns: 'billing' })}
        message={error.message || i18next.t('unableToLoad', { ns: 'billing' })}
      />
    </PageShell>
  ),
})

function BillingPeriodRoute() {
  const { billingPeriodId } = Route.useParams()
  return (
    // Keyed by period so an error for one period doesn't stick after switching to another.
    // The error page keeps the period picker: "Back" to /billing would redirect to the default
    // period, which may be the very one failing.
    <QueryBoundary
      key={billingPeriodId}
      loadingFallback={<BillingOverviewLoading />}
      errorTitle={i18next.t('failedToLoad', { ns: 'billing' })}
      errorMessage={i18next.t('unableToLoad', { ns: 'billing' })}
      pageTitle={i18next.t('title', { ns: 'billing' })}
      fullWidth
      errorPageAction={<BillingPeriodSwitcher value={Number(billingPeriodId)} />}
    >
      <BillingOverviewPage billingPeriodId={Number(billingPeriodId)} />
    </QueryBoundary>
  )
}
